import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import { IconButton } from 'react-native-paper';
import { MenuItem as MenuItemType } from '../types';
import { getValidIconName } from '../../util.style';

interface MenuItemProps {
  item: MenuItemType;
  isCollapsed: boolean;
  textColor: string;
  activeColor: string;
  hoverColor: string;
  activeOpacity?: number;
  onPress?: (item: MenuItemType) => void;
  isActive?: boolean;
  fontFamily?: string;
  isCanvasMode?: boolean;
  activeItemId?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isCollapsed,
  textColor,
  activeColor,
  hoverColor,
  activeOpacity = 0.3,
  onPress,
  isActive = false,
  fontFamily,
  isCanvasMode = false,
  activeItemId,
}) => {
  const [isGroupExpanded, setIsGroupExpanded] = useState(true);

  if (item.hidden) return null;

  if (item.divider) {
    return (
      <View
        style={{
          height: 1,
          opacity: 0.2,
          marginVertical: 8,
          marginHorizontal: isCollapsed ? 8 : 16,
        }}
      />
    );
  }

  // Handle group items
  if (item.type === 'group' && item.children) {
    return (
      <View style={{ marginBottom: 16 }}>
        {/* Group header */}
        {!isCollapsed && (
          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 8,
              paddingHorizontal: 16,
              marginVertical: 4,
              paddingLeft: 43,
            }}
            onPress={() => setIsGroupExpanded(!isGroupExpanded)}
            disabled={false}
          >
            <Text
              style={{
                color: textColor,
                fontSize: 12,
                fontFamily: fontFamily,
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                opacity: 0.8,
                flex: 1,
              }}
            >
              {item.label}
            </Text>
            <IconButton
              icon={isGroupExpanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={textColor}
              style={{ margin: 0, opacity: 0.6 }}
            />
          </TouchableOpacity>
        )}

        {/* Group children */}
        {isGroupExpanded &&
          item.children.map((child) => (
            <MenuItemComponent
              key={child.id}
              item={child}
              isCollapsed={isCollapsed}
              textColor={textColor}
              activeColor={activeColor}
              hoverColor={hoverColor}
              activeOpacity={activeOpacity}
              onPress={onPress}
              isActive={activeItemId === child.id}
              fontFamily={fontFamily}
              isCanvasMode={isCanvasMode}
              activeItemId={activeItemId}
            />
          ))}
      </View>
    );
  }

  // Handle regular menu items
  return (
    <MenuItemComponent
      item={item}
      isCollapsed={isCollapsed}
      textColor={textColor}
      activeColor={activeColor}
      hoverColor={hoverColor}
      activeOpacity={activeOpacity}
      onPress={onPress}
      isActive={isActive}
      fontFamily={fontFamily}
      isCanvasMode={isCanvasMode}
      activeItemId={activeItemId}
    />
  );
};

// Individual menu item component
const MenuItemComponent: React.FC<MenuItemProps> = ({
  item,
  isCollapsed,
  textColor,
  activeColor,
  hoverColor,
  activeOpacity = 0.3,
  onPress,
  isActive = false,
  isCanvasMode = false,
  fontFamily,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Parse colors to apply opacity for active and hover states
  const getBackgroundColor = () => {
    if (isActive) {
      if (activeColor.includes('rgba')) return activeColor;

      // If it's a hex color, convert to rgba with opacity
      if (activeColor.startsWith('#')) {
        const hex = activeColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${activeOpacity})`;
      }

      // For other formats, use as is
      return activeColor;
    }

    if (isHovered && !isCanvasMode) {
      return hoverColor;
    }

    return 'transparent';
  };

  const itemStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingVertical: 8,
    paddingHorizontal: isCollapsed ? 8 : 16,
    marginVertical: 2,
    marginHorizontal: 18,
    borderRadius: 7,
    backgroundColor: getBackgroundColor(),
    opacity: item.disabled ? 0.5 : 1,
  };

  const textStyle = {
    color: textColor,
    fontSize: 14,
    fontFamily: fontFamily,
    fontWeight: '500' as const,
    flex: 1,
    textAlign: 'left' as const,
  };

  const badgeStyle = {
    backgroundColor: activeColor,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };

  const badgeTextStyle = {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: fontFamily,
    fontWeight: 'bold' as const,
  };

  return (
    <TouchableOpacity
      style={itemStyle}
      onPress={() => !item.disabled && !isCanvasMode && onPress?.(item)}
      disabled={item.disabled || isCanvasMode}
      {...(Platform.OS === 'web' &&
        !isCanvasMode && {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        })}
    >
      {/* Icon container - always present to maintain consistent spacing */}
      <View
        style={{
          width: 40,
          height: 20,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {item.icon && (
          <IconButton
            icon={getValidIconName(item.icon, isCanvasMode, 'paper')}
            size={18}
            color={textColor}
            style={{ margin: 0 }}
          />
        )}
      </View>

      {!isCollapsed && (
        <>
          <Text style={textStyle} numberOfLines={1}>
            {item.label}
          </Text>

          {item.badge && (
            <View style={badgeStyle}>
              <Text style={badgeTextStyle}>
                {typeof item.badge === 'number' && item.badge > 99
                  ? '99+'
                  : item.badge}
              </Text>
            </View>
          )}
        </>
      )}
    </TouchableOpacity>
  );
};
